import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const statusColors = {
  passed: 'success',
  failed: 'error',
  skipped: 'warning',
  pending: 'default',
  running: 'info',
};

const statusIcons = {
  passed: <CheckCircleIcon color="success" />,
  failed: <ErrorIcon color="error" />,
  skipped: <WarningIcon color="warning" />,
  pending: <ScheduleIcon color="disabled" />,
  running: <CircularProgress size={20} />,
};

const HealthCheckPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [greenfieldLoading, setGreenfieldLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [fullTest, setFullTest] = useState(false);
  const [cleanup, setCleanup] = useState(true);
  const [lastRun, setLastRun] = useState(null);

  // Regression checks (same as the L2 deploy gate, on-demand from UI)
  const [regressionLoading, setRegressionLoading] = useState(null); // 'schema' | 'heads' | 'smoke' | 'all' | null
  const [regressionResults, setRegressionResults] = useState(null);
  const [regressionError, setRegressionError] = useState(null);

  // Run quick health check on mount
  useEffect(() => {
    runQuickCheck();
  }, []);

  const runQuickCheck = async () => {
    setQuickLoading(true);
    setError(null);
    try {
      const response = await api.get('/health/e2e');
      setResults(response.data);
      setLastRun(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Health check failed');
    } finally {
      setQuickLoading(false);
    }
  };

  const runFullTest = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/health/e2e', {
        full_test: fullTest,
        cleanup: cleanup,
      });
      setResults(response.data);
      setLastRun(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'E2E test failed');
    } finally {
      setLoading(false);
    }
  };

  // Greenfield International content-validated E2E (provision -> Q1-Q13 -> offboard).
  // Backend: POST /api/v1/health/e2e/greenfield. Contained Starter-tier test tenant.
  const runGreenfieldTest = async () => {
    setGreenfieldLoading(true);
    setError(null);
    try {
      const response = await api.post('/health/e2e/greenfield', { cleanup: true });
      setResults(response.data);
      setLastRun(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Greenfield E2E failed');
    } finally {
      setGreenfieldLoading(false);
    }
  };

  // ── Regression checks (Phase 1+2 from regression-test-rollout plan) ─────
  // Backend endpoints: POST /api/v1/health/regression/{schema-audit, alembic-heads, smoke, all}
  // Same shape as the deploy workflow gates, run on demand.
  const runRegression = async (kind) => {
    const endpointMap = {
      schema: '/health/regression/schema-audit',
      heads: '/health/regression/alembic-heads',
      smoke: '/health/regression/smoke',
      all: '/health/regression/all',
    };
    setRegressionLoading(kind);
    setRegressionError(null);
    try {
      const response = await api.post(endpointMap[kind]);
      // For 'all' we get nested results; for atomic kinds we get a single check.
      setRegressionResults({ kind, data: response.data, ran_at: new Date() });
    } catch (err) {
      setRegressionError(err.response?.data?.detail || err.message || `${kind} check failed`);
    } finally {
      setRegressionLoading(null);
    }
  };

  const downloadResults = () => {
    if (!results) return;
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `health-check-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getOverallStatusColor = () => {
    if (!results) return 'default';
    if (results.status === 'passed') return 'success';
    if (results.status === 'partial') return 'warning';
    return 'error';
  };

  const getOverallStatusIcon = () => {
    if (!results) return <ScheduleIcon />;
    if (results.status === 'passed') return <CheckCircleIcon />;
    if (results.status === 'partial') return <WarningIcon />;
    return <ErrorIcon />;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🏥 Platform Health Check
          </Typography>
          <Typography variant="body2" color="text.secondary">
            End-to-end testing for Serviti platform - works on local and Azure cloud
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/health-check/smoke-history')}
          >
            Smoke Test History →
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/platform/conversation-audit')}
          >
            Audit Conversations →
          </Button>
          <Tooltip title="Download Results">
            <IconButton onClick={downloadResults} disabled={!results}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Quick Refresh">
            <IconButton onClick={runQuickCheck} disabled={quickLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Status Card */}
      {results && (
        <Card sx={{ mb: 3, bgcolor: `${getOverallStatusColor()}.light`, color: `${getOverallStatusColor()}.contrastText` }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                {getOverallStatusIcon()}
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ textTransform: 'uppercase' }}>
                  {results.status}
                </Typography>
                <Typography variant="body2">
                  {results.summary?.passed}/{results.summary?.total} tests passed
                  {results.summary?.failed > 0 && ` • ${results.summary.failed} failed`}
                  {results.summary?.skipped > 0 && ` • ${results.summary.skipped} skipped`}
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    <SpeedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {results.duration_seconds}s
                  </Typography>
                  <Typography variant="body2">
                    {results.environment === 'local' ? (
                      <><ComputerIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} /> Local</>
                    ) : (
                      <><CloudIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} /> Azure</>
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={fullTest}
                onChange={(e) => setFullTest(e.target.checked)}
                disabled={loading}
              />
            }
            label="Full Workflow Test"
          />
          {fullTest && (
            <FormControlLabel
              control={
                <Switch
                  checked={cleanup}
                  onChange={(e) => setCleanup(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Auto Cleanup"
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
            onClick={runFullTest}
            disabled={loading || quickLoading || greenfieldLoading}
          >
            {loading ? 'Running Tests...' : (fullTest ? 'Run Full E2E Test' : 'Run Quick Check')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={greenfieldLoading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
            onClick={runGreenfieldTest}
            disabled={loading || quickLoading || greenfieldLoading}
            sx={{ ml: 1 }}
          >
            {greenfieldLoading ? 'Running Greenfield...' : 'Greenfield E2E'}
          </Button>
        </Box>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Greenfield E2E</strong> provisions a contained Starter-tier test tenant from the
          Greenfield handbook PDF, validates Q1-Q13 (incl. anti-hallucination) through a RAG agent,
          sends a real lead-notification test (email to krishna@serviti.ai + WhatsApp to the team
          test number), then deletes the tenant. Makes real LLM, embedding, email and WhatsApp
          calls — run on demand.
        </Alert>
        
        {fullTest && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Full workflow test will create a test tenant, user, collection, upload documents, 
            test web scraping, and verify chat functionality. 
            {cleanup ? ' All test data will be automatically cleaned up.' : ' Test data will NOT be cleaned up.'}
          </Alert>
        )}
      </Paper>

      {/* ─── Regression Tests (Phase 1+2 from regression-test-rollout plan) ─── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Regression Tests
          </Typography>
          <Chip label="Manual trigger" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Same checks the deploy workflow runs against every push - run on demand to validate
          this environment without waiting for a deploy. Read-only and safe to run anytime.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => runRegression('schema')}
            disabled={regressionLoading !== null}
            startIcon={regressionLoading === 'schema' ? <CircularProgress size={16} /> : <StorageIcon />}
          >
            Schema Audit
          </Button>
          <Button
            variant="outlined"
            onClick={() => runRegression('heads')}
            disabled={regressionLoading !== null}
            startIcon={regressionLoading === 'heads' ? <CircularProgress size={16} /> : <StorageIcon />}
          >
            Alembic Heads
          </Button>
          <Button
            variant="outlined"
            onClick={() => runRegression('smoke')}
            disabled={regressionLoading !== null}
            startIcon={regressionLoading === 'smoke' ? <CircularProgress size={16} /> : <SpeedIcon />}
          >
            Endpoint Smoke
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            onClick={() => runRegression('all')}
            disabled={regressionLoading !== null}
            startIcon={regressionLoading === 'all' ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
          >
            Run All Regression Checks
          </Button>
        </Box>

        {regressionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRegressionError(null)}>
            {regressionError}
          </Alert>
        )}

        {regressionResults && (
          <Box sx={{ mt: 1 }}>
            {/* Atomic checks return a single result; 'all' returns nested results */}
            {(() => {
              const { kind, data, ran_at } = regressionResults;
              const renderSingle = (r) => {
                if (!r) return null;
                return (
                  <Alert
                    key={r.check}
                    severity={r.passed ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  >
                    <strong>{r.check}:</strong> {r.message || (r.passed ? 'OK' : 'FAILED')}
                    {r.missing_columns?.length > 0 && (
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        {r.missing_columns.map((c) => <li key={c}><code>{c}</code></li>)}
                      </Box>
                    )}
                    {r.missing_tables?.length > 0 && (
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        {r.missing_tables.map((t) => <li key={t}><code>{t}</code> (table)</li>)}
                      </Box>
                    )}
                    {r.file_heads && (
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        file heads: {r.file_heads.join(', ') || '(none)'} · db versions: {r.db_versions?.join(', ') || '(none)'}
                      </Typography>
                    )}
                    {r.checks?.length > 0 && (
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        {r.checks.map((c) => (
                          <li key={c.endpoint}>
                            {c.passed ? '✓' : '✗'} <code>{c.endpoint}</code>
                            {c.status_code != null && ` → ${c.status_code}`}
                            {c.error && ` (${c.error})`}
                          </li>
                        ))}
                      </Box>
                    )}
                    {r.error && (
                      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                        {r.error}
                      </Typography>
                    )}
                  </Alert>
                );
              };
              if (kind === 'all') {
                return (
                  <>
                    <Alert
                      severity={data.passed ? 'success' : 'error'}
                      icon={data.passed ? <CheckCircleIcon /> : <ErrorIcon />}
                      sx={{ mb: 1 }}
                    >
                      <strong>Overall:</strong> {data.passed ? 'All regression checks passed.' : 'One or more regression checks failed.'}
                    </Alert>
                    {renderSingle(data.schema_audit)}
                    {renderSingle(data.alembic_heads)}
                    {renderSingle(data.smoke)}
                    <Typography variant="caption" color="text.secondary">
                      Ran at {ran_at.toLocaleString()}
                    </Typography>
                  </>
                );
              }
              return (
                <>
                  {renderSingle(data)}
                  <Typography variant="caption" color="text.secondary">
                    Ran at {ran_at.toLocaleString()}
                  </Typography>
                </>
              );
            })()}
          </Box>
        )}
      </Paper>

      {/* Loading indicator */}
      {(loading || quickLoading || greenfieldLoading) && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Running health checks... This may take a minute for full tests.
          </Typography>
        </Box>
      )}

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Test Results */}
      {results?.tests && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {lastRun && `Last run: ${lastRun.toLocaleString()}`}
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40">Status</TableCell>
                  <TableCell>Test Name</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell width="100" align="right">Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.tests.map((test, index) => (
                  <React.Fragment key={index}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: test.status === 'failed' ? 'error.light' : 'inherit'
                      }}
                    >
                      <TableCell>
                        {statusIcons[test.status] || statusIcons.pending}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {test.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {test.message}
                        </Typography>
                        {test.error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            Error: {test.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          size="small" 
                          label={`${test.duration_ms}ms`}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                    {/* Details accordion if available */}
                    {test.details && Object.keys(test.details).length > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 'none' }}>
                          <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="caption" color="text.secondary">
                                View Details
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <pre className="health-check-details" style={{ 
                                margin: 0, 
                                fontSize: '12px', 
                                padding: '8px', 
                                borderRadius: '4px',
                                overflow: 'auto'
                              }}>
                                {JSON.stringify(test.details, null, 2)}
                              </pre>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Test Categories Legend */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Test Categories
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip icon={<StorageIcon />} label="Infrastructure (Quick)" size="small" variant="outlined" />
          <Chip icon={<CloudIcon />} label="Tenant & Auth" size="small" variant="outlined" />
          <Chip label="Collection & Documents" size="small" variant="outlined" />
          <Chip label="Web Scraping" size="small" variant="outlined" />
          <Chip label="Chat/RAG" size="small" variant="outlined" />
          <Chip label="Cleanup" size="small" variant="outlined" />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Quick Check:</strong> Tests API, Database, and Vector DB connectivity only.<br />
          <strong>Full E2E Test:</strong> Creates test tenant, uploads documents, tests scraping and chat, then cleans up.
        </Typography>
      </Paper>
    </Box>
  );
};

export default HealthCheckPage;
